import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import TagBadge from "@/components/TagBadge";
import {
  displayName,
  initials,
  formatRelative,
  formatNumber,
} from "@/lib/utils-format";
import {
  MagnifyingGlass,
  Funnel,
  Check,
  X,
  TelegramLogo,
  CaretLeft,
  CaretRight,
} from "@phosphor-icons/react";
import CustomerDrawer from "@/components/CustomerDrawer";

export default function Subscribers() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filters, setFilters] = useState({
    event_id: "",
    tag_id: "",
    has_email: "",
    blocked: "",
  });
  const [page, setPage] = useState(0);
  const [pageSize] = useState(20);
  const [events, setEvents] = useState([]);
  const [tags, setTags] = useState([]);
  const [openUserId, setOpenUserId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const params = { limit: pageSize, offset: page * pageSize };
    if (q.trim()) params.q = q.trim();
    if (filters.event_id) params.event_id = filters.event_id;
    if (filters.tag_id) params.tag_id = filters.tag_id;
    if (filters.has_email !== "") params.has_email = filters.has_email;
    if (filters.blocked !== "") params.blocked = filters.blocked;
    const { data } = await api.get("/subscribers", { params });
    setItems(data.items);
    setTotal(data.total);
    setLoading(false);
  }, [q, filters, page, pageSize]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    Promise.all([api.get("/events"), api.get("/tags")]).then(
      ([e, t]) => {
        setEvents(e.data);
        setTags(t.data);
      }
    );
  }, []);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div data-testid="subscribers-page">
      <PageHeader
        title="Подписчики"
        subtitle={`${formatNumber(total)} пользователей в базе`}
      />

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-[240px]">
          <MagnifyingGlass
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500"
          />
          <input
            data-testid="search-input"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setPage(0);
            }}
            placeholder="Поиск по имени, username, ID…"
            className="w-full bg-zinc-900 border border-zinc-800 rounded-md pl-9 pr-3 py-2 text-sm
                       focus:outline-none focus:border-blue-500/60"
          />
        </div>
        <select
          data-testid="filter-event"
          value={filters.event_id}
          onChange={(e) => {
            setFilters({ ...filters, event_id: e.target.value });
            setPage(0);
          }}
          className="bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2 text-sm"
        >
          <option value="">Все ивенты</option>
          {events.map((ev) => (
            <option key={ev.id} value={ev.id}>
              {ev.btn_name}
            </option>
          ))}
        </select>
        <select
          data-testid="filter-tag"
          value={filters.tag_id}
          onChange={(e) => {
            setFilters({ ...filters, tag_id: e.target.value });
            setPage(0);
          }}
          className="bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2 text-sm"
        >
          <option value="">Все теги</option>
          {tags.map((t) => (
            <option key={t.id} value={t.id}>
              {t.tag_type}: {t.name}
            </option>
          ))}
        </select>
        <select
          data-testid="filter-email"
          value={filters.has_email}
          onChange={(e) => {
            setFilters({ ...filters, has_email: e.target.value });
            setPage(0);
          }}
          className="bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2 text-sm"
        >
          <option value="">Email: любой</option>
          <option value="true">Есть email</option>
          <option value="false">Без email</option>
        </select>
        <select
          data-testid="filter-blocked"
          value={filters.blocked}
          onChange={(e) => {
            setFilters({ ...filters, blocked: e.target.value });
            setPage(0);
          }}
          className="bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2 text-sm"
        >
          <option value="">Бот: любой</option>
          <option value="false">Активен</option>
          <option value="true">Заблокирован</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-zinc-900/40 border border-zinc-800 rounded-lg overflow-hidden">
        <table className="w-full text-sm" data-testid="subscribers-table">
          <thead>
            <tr className="border-b border-zinc-800 text-[10px] uppercase tracking-[0.15em] text-zinc-500">
              <th className="text-left px-4 py-3 font-medium">Пользователь</th>
              <th className="text-left px-4 py-3 font-medium">Telegram ID</th>
              <th className="text-left px-4 py-3 font-medium">Регистрации</th>
              <th className="text-left px-4 py-3 font-medium">Канал</th>
              <th className="text-left px-4 py-3 font-medium">Бот</th>
              <th className="text-left px-4 py-3 font-medium">Источник</th>
              <th className="text-left px-4 py-3 font-medium">Теги</th>
              <th className="text-left px-4 py-3 font-medium">Активность</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={8} className="text-center py-12 text-zinc-500">
                  Загрузка…
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={8} className="text-center py-12 text-zinc-500">
                  Подписчики не найдены
                </td>
              </tr>
            ) : (
              items.map((u) => (
                <tr
                  key={u.user_id}
                  data-testid={`subscriber-row-${u.user_id}`}
                  onClick={() => setOpenUserId(u.user_id)}
                  className="border-b border-zinc-900 hover:bg-white/[0.02] transition-colors cursor-pointer"
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-zinc-700 to-zinc-800 flex items-center justify-center text-xs font-semibold border border-zinc-700">
                        {initials(u)}
                      </div>
                      <div className="min-w-0">
                        <div className="font-medium truncate max-w-[160px]">
                          {displayName(u)}
                        </div>
                        {u.username && (
                          <div className="text-xs text-zinc-500 font-mono flex items-center gap-1">
                            <TelegramLogo size={10} weight="fill" />@{u.username}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 font-mono text-zinc-400 text-xs">
                    {u.user_id}
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-mono text-zinc-300">
                      {u.regs_count || 0}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <ChannelBadge status={u.main_channel_status} />
                  </td>
                  <td className="px-4 py-3">
                    <BotBadge blocked={u.is_bot_blocked} />
                  </td>
                  <td className="px-4 py-3 text-zinc-400 font-mono text-xs">
                    {u.utm_source || "—"}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1 max-w-[200px]">
                      {u.tags?.slice(0, 3).map((t) => (
                        <TagBadge key={t.id} tag={t} />
                      ))}
                      {u.tags?.length > 3 && (
                        <span className="text-xs text-zinc-500">
                          +{u.tags.length - 3}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs text-zinc-500">
                    {formatRelative(u.last_activity || u.last_reg)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between mt-4 text-sm text-zinc-500">
        <div>
          Страница {page + 1} из {totalPages}
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="prev-page-btn"
            disabled={page === 0}
            onClick={() => setPage((p) => p - 1)}
            className="px-3 py-1.5 rounded-md border border-zinc-800 hover:border-zinc-700 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <CaretLeft size={12} weight="bold" />
          </button>
          <button
            data-testid="next-page-btn"
            disabled={page + 1 >= totalPages}
            onClick={() => setPage((p) => p + 1)}
            className="px-3 py-1.5 rounded-md border border-zinc-800 hover:border-zinc-700 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <CaretRight size={12} weight="bold" />
          </button>
        </div>
      </div>

      {openUserId && (
        <CustomerDrawer
          userId={openUserId}
          onClose={() => setOpenUserId(null)}
          onChanged={load}
        />
      )}
    </div>
  );
}

function ChannelBadge({ status }) {
  if (status === "subscribed")
    return (
      <span className="badge-dot text-[#2AABEE] text-xs font-mono inline-flex items-center">
        подписан
      </span>
    );
  if (status === "not_subscribed")
    return (
      <span className="badge-dot text-zinc-500 text-xs font-mono inline-flex items-center">
        не подписан
      </span>
    );
  return (
    <span className="text-xs text-zinc-600 font-mono">—</span>
  );
}

function BotBadge({ blocked }) {
  if (blocked)
    return (
      <span className="text-red-400 text-xs font-mono inline-flex items-center gap-1">
        <X size={10} weight="bold" /> блок
      </span>
    );
  return (
    <span className="text-emerald-400 text-xs font-mono inline-flex items-center gap-1">
      <Check size={10} weight="bold" /> активен
    </span>
  );
}
